import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { EmissionsByTocResponse, formatNumber } from './types';
import ChartErrorState from './ChartErrorState';
import { formatDecimal } from '../../utils/localeFormat';
import { chartPalette, chartChrome } from '../../utils/chartColors';
import './EmissionsByTocChart.scss';

interface EmissionsByTocChartProps {
  data: EmissionsByTocResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

// Sentinel `toc` key for shipments with no TOC at all — not a real category,
// see types.ts. Compared against `toc` (the canonical key), never `label`.
const UNKNOWN_TOC = 'Unknown';

// Donut weakness #2 (long free-text labels break layout): cap what the
// legend shows inline and rely on the legend's own hover tooltip — which we
// wire up below with the untruncated name — to recover the full text.
const LEGEND_LABEL_MAX = 22;
const truncateLabel = (label: string): string =>
  label.length > LEGEND_LABEL_MAX ? `${label.slice(0, LEGEND_LABEL_MAX - 1)}…` : label;

const EmissionsByTocChart: FC<EmissionsByTocChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.overCap || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const palette = chartPalette();
    const chrome = chartChrome();
    const items = data.data;
    const total = items.reduce((sum, d) => sum + d.currentValue, 0);

    // Donut weakness #3 (the Unknown slice is not a real category): give it
    // a muted grey rather than a rotation color, and keep the palette index
    // advancing only for genuine categories so Unknown's position in the
    // (already value-sorted) data never shifts which color a real TOC gets.
    let paletteIdx = 0;
    const seriesData = items.map((d) => {
      const isUnknown = d.toc === UNKNOWN_TOC;
      let color: string;
      if (isUnknown) {
        color = chrome.muted;
      } else {
        color = palette[paletteIdx % palette.length];
        paletteIdx += 1;
      }
      const percentage = total > 0 ? (d.currentValue / total) * 100 : 0;
      return {
        name: d.label,
        value: d.currentValue,
        percentage,
        itemStyle: { color },
      };
    });

    // Donut weakness #1 (hides absolute values): the slice label shows only
    // the percentage (consistent with EmissionSourceDistributionChart and
    // short enough to fit on a wedge), but both the slice tooltip and the
    // legend hover tooltip below surface the absolute kg value.
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          `${params.name}: ${formatNumber(params.value)} kg CO2e (${formatDecimal(params.data.percentage, 1, lng)}%)`,
      },
      legend: {
        // Donut weakness #4 (up to ~10 slices can overflow the legend box):
        // a scrolling legend paginates instead of clipping.
        type: 'scroll',
        orient: 'horizontal',
        bottom: '0%',
        left: 'center',
        textStyle: { color: chrome.text, fontSize: 13 },
        formatter: (name: string) => truncateLabel(name),
        tooltip: {
          show: true,
          formatter: (params: any) => {
            const item = seriesData.find((d) => d.name === params.name);
            return item
              ? `${params.name}: ${formatNumber(item.value)} kg CO2e (${formatDecimal(item.percentage, 1, lng)}%)`
              : params.name;
          },
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: chrome.surface,
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: (params: any) => `${formatDecimal(params.data.percentage, 1, lng)}%`,
            color: chrome.text,
            fontSize: 12,
          },
          data: seriesData,
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, lng]);

  // Handle resize & cleanup
  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="emissions-by-toc-chart">
        <h3 className="emissions-by-toc-chart__title">{t('cpi.analytics.emissionsByToc')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="emissions-by-toc-chart emissions-by-toc-chart--loading">
        <h3 className="emissions-by-toc-chart__title">{t('cpi.analytics.emissionsByToc')}</h3>
        <div className="emissions-by-toc-chart__skeleton" data-testid="emissions-by-toc-chart-skeleton" />
      </div>
    );
  }

  if (data && data.overCap) {
    return (
      <div className="emissions-by-toc-chart">
        <h3 className="emissions-by-toc-chart__title">{t('cpi.analytics.emissionsByToc')}</h3>
        <p className="chart-no-data">{t('cpi.analytics.emissionsByTocOverCap')}</p>
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="emissions-by-toc-chart">
        <h3 className="emissions-by-toc-chart__title">{t('cpi.analytics.emissionsByToc')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="emissions-by-toc-chart">
      <h3 className="emissions-by-toc-chart__title">{t('cpi.analytics.emissionsByToc')}</h3>
      <div
        ref={chartCallbackRef}
        data-testid="emissions-by-toc-chart-canvas"
        style={{ height: '360px', width: '100%' }}
      />
    </div>
  );
};

export default EmissionsByTocChart;
